<template>
    <div id="app">
        <DisabledCookieBlock
          v-if="showDisabledCookieBlock"
        ></DisabledCookieBlock>
        <div v-else class="grid-wrap" ref="shakedInputArea">
            <form-page class="grid-row" v-if="!completed">
                <template v-slot:titleLeft>
                    <h1>{{ $t("form.register.header") }}</h1>
                </template>
                <template v-slot:titleRight>
                    <div class="header-rigth">
                        <span class="login-description">{{
                            $t("header.account.createDescription")
                        }}</span>
                        <a
                            :href="actions.loginUrl"
                            class="button button_size_medium button_type_standart button_family_secondary button_color_primary"
                        >
                            <div class="button__text">
                                {{ $t("header.account.create") }}
                            </div>
                        </a>
                    </div>
                </template>
                <template v-slot:default>
                    <LabeledFormControl>
                        <template v-slot:labelLeft>{{
                            $t("form.firstName.label")
                        }}</template>
                        <template v-slot:default>
                            <base-input
                                :placeholder="$t('form.firstName.placeholder')"
                                :state="firstNameState"
                                name="firstName"
                                ref="firstNameControl"
                                v-model="$v.firstName.$model"
                            />
                        </template>
                        <template
                            v-slot:error
                            v-if="$v.firstName.$error && canValidate"
                            >{{
                                $v.firstName.alphaCyrillic
                                    ? $t("validation.firstName.require")
                                    : $t("validation.firstName.alphaCyrillic")
                            }}</template
                        >
                    </LabeledFormControl>
                    <LabeledFormControl>
                        <template v-slot:labelLeft>{{
                            $t("form.lastName.label")
                        }}</template>
                        <template v-slot:default>
                            <base-input
                                :placeholder="$t('form.lastName.placeholder')"
                                :state="lastNameState"
                                name="lastName"
                                ref="lastNameControl"
                                v-model="$v.lastName.$model"
                            />
                        </template>
                        <template
                            v-slot:error
                            v-if="$v.lastName.$error && canValidate"
                            >{{
                                $v.lastName.alphaCyrillic
                                    ? $t("validation.lastName.require")
                                    : $t("validation.lastName.alphaCyrillic")
                            }}</template
                        >
                    </LabeledFormControl>
                    <LabeledFormControl>
                        <template v-slot:labelLeft>{{
                            $t("form.email.label")
                        }}</template>
                        <template v-slot:default>
                            <email-control
                                class="additional-control"
                                :placeholder="$t('form.email.placeholder')"
                                :includedValidation="includedEmailValidation"
                                position="left"
                                ref="emailControl"
                            />
                        </template>
                    </LabeledFormControl>
                    <LabeledFormControl>
                        <template v-slot:labelLeft>{{
                            $t("form.password.label")
                        }}</template>
                        <template v-slot:default>
                            <password-control
                                class="additional-control"
                                v-model="password"
                                ref="passwordControl"
                                position="left"
                                :includedValidation="includedPasswordValidation"
                                :placeholder="$t('form.password.placeholder')"
                                :minLength="6"
                            ></password-control>
                        </template>
                    </LabeledFormControl>
                    <div class="birthdate-wrapper">
                        <LabeledFormControl>
                            <template v-slot:labelLeft>{{
                                $t("form.birthdate.label")
                            }}</template>
                            <template v-slot:default>
                              <div style="width: 100%">
                                <DateInput
                                  v-model="birthDate"
                                  :class="{'input_state_incorrect': dateValidation.hasErrors}"
                                  id="birthDate"
                                  minDate="1870-01-01"
                                  :maxDate="`${new Date().getFullYear()}-01-01`"
                                  :showErrors="canValidate"
                                  @validated="dateValidation = $event"
                                ></DateInput>
                              </div>
                            </template>
                            <template
                              v-slot:error
                              v-if="dateValidation.hasErrors && canValidate"
                            >
                              <div v-if="!dateValidation.required">{{ $t("validation.birthDate.require") }}</div>
                              <div v-else>{{ $t("validation.birthDate.incorrect") }}</div>
                            </template>
                        </LabeledFormControl>
                    </div>
                    <LabeledFormControl>
                        <template v-slot:labelLeft>{{
                            $t("form.gender.label")
                        }}</template>
                        <template v-slot:default>
                            <div class="gender">
                                <BaseRadio
                                    name="gender"
                                    class="choice-group-item__radio"
                                    value="male"
                                    v-model="gender"
                                    :state="genderState"
                                >
                                    <div class="item-radio__title">
                                        {{ $t("form.gender.options.male") }}
                                    </div>
                                </BaseRadio>
                                <BaseRadio
                                    name="gender"
                                    class="choice-group-item__radio"
                                    value="female"
                                    v-model="gender"
                                    :state="genderState"
                                >
                                    <div class="item-radio__title">
                                        {{ $t("form.gender.options.female") }}
                                    </div>
                                </BaseRadio>
                            </div>
                        </template>
                        <template v-slot:error v-if="!gender && canValidate">{{
                            $t("validation.gender.require")
                        }}</template>
                    </LabeledFormControl>
                    <base-alert
                        v-if="error"
                        :text="error"
                        :hasIcon="true"
                        state="danger"
                        class="choice-group__alert"
                    ></base-alert>
                    <div class="form-page__divider"></div>
                    <base-checkbox v-model="isAgree" v-on:change="agree">
                        <span class="terms" v-html="termsHtml()"></span>
                    </base-checkbox>
                    <base-checkbox
                      v-if="visibleSendPromoEmails"
                      v-model="sendPromoEmails"
                      style="margin-top: 1em;"
                    >
                      {{ $t('form.promoEmailsCheckbox') }}
                    </base-checkbox>
                    <div class="form-page__buttons-area">
                        <base-button
                            family="primary"
                            size="medium"
                            class="register"
                            :disabled="
                                (!isValid && canValidate) ||
                                !isAgree ||
                                !canRegister
                            "
                            :loading="loading"
                            :action="validate"
                            >{{ $t("form.action.register") }}</base-button
                        >
                        <div
                            class="g-recaptcha"
                            :data-sitekey="recaptchaKey"
                            data-callback="onSubmit"
                            data-size="invisible"
                        ></div>
                    </div>
                </template>
            </form-page>
            <form-page class="grid-row__size-16" v-if="completed">
                <template v-slot:titleLeft>
                    <h1>{{ $t("form.successed.header") }}</h1>
                </template>
                <template v-slot:default>
                    <p class="to-info">{{ $t("form.successed.info") }}</p>
                    <p class="to-email">
                        <b>{{ $refs.emailControl.emailValue }}</b>
                    </p>
                    <div class="form-page__plate">
                        <p>{{ $t("form.successed.warning.title") }}</p>
                        <ul>
                            <li>{{ $t("form.successed.warning.case") }}</li>
                        </ul>
                    </div>
                </template>
            </form-page>
        </div>
        <DisabledCookieAlert ref="disabledCookieAlert" v-show="!showDisabledCookieBlock"></DisabledCookieAlert>
    </div>
</template>

<script>
import ShakeErrorInputMixin from "./../../assets/mixins/ShakeErrorInputMixin";
import ControlValidationMixin from "./../../assets/mixins/ControlValidationMixin";
import FormPage from "./../../assets/components/FormPage";
import BaseButton from "./../../assets/components/BaseButton";
import BaseInput from "./../../assets/components/BaseInput";
import BaseAlert from "./../../assets/components/BaseAlert";
import BaseCheckbox from "./../../assets/components/BaseCheckbox";
import BaseRadio from "./../../assets/components/BaseRadio";
import LabeledFormControl from "./../../assets/components/LabeledFormControl";
import EmailControl from "./../../assets/components/EmailControl";
import PasswordControl from "./../../assets/components/PasswordControl";
import { required, helpers } from "vuelidate/lib/validators";
import DateInput from '../../main_old/components/DateInput/DateInput.vue';
import DisabledCookieAlert from '../../assets/components/DisabledCookieAlert/DisabledCookieAlert.vue';
import DisabledCookieBlock from '../../assets/components/DisabledCookieBlock/DisabledCookieBlock.vue';

import axios from "axios";

const alphaCyrillic = helpers.regex('alphaCyrillic', /^[a-zA-ZА-ЯЁа-яё\s-]*$/);

export default {
    name: "CreateAccount",
    components: {
        FormPage,
        BaseButton,
        LabeledFormControl,
        BaseInput,
        EmailControl,
        PasswordControl,
        BaseRadio,
        BaseCheckbox,
        BaseAlert,
        DateInput,
        DisabledCookieAlert,
        DisabledCookieBlock,
    },
    mixins: [ShakeErrorInputMixin, ControlValidationMixin],
    i18n: {
      messages: {
        ru: {
          email_no_cyrillic: 'Электронные почты, содержащие символы кириллицы, не&nbsp;поддерживаются. Используйте другую почту.',
        },
        en: {
          email_no_cyrillic: 'Emails containing Cyrillic characters are not&nbsp;supported. Please use a&nbsp;different email.',
        },
        uz: {
          email_no_cyrillic: 'Kirillcha belgilarni o‘z ichiga olgan elektron pochta xabarlari qo‘llab-quvvatlanmaydi. Boshqa elektron pochta manzilini kiriting.',
        },
      },
    },
    data() {
        const { actions, returnUrl, recaptchaKey } = window.vm;

        return {
            showDisabledCookieBlock: false,
            self: this,
            firstName: "",
            lastName: "",
            email: "",
            emailChecking: false,
            birthDate: null,
            password: "",
            gender: null,
            canRegister: false,
            canValidate: false,
            isAgree: false,
            sendPromoEmails: false,
            loading: false,
            completed: false,
            dateValidation: {},
            error: null,
            includedBirthDateValidation: {
                required: this.$t("validation.birthDate.require"),
                date: this.$t("validation.birthDate.incorrect"),
            },
            includedEmailValidation: {
                required: this.$t("validation.email.require"),
                email: this.$t("validation.email.incorrect"),
                exists: this.$t("validation.email.exists"),
                noCyrillic: this.$t('email_no_cyrillic'),
            },
            includedPasswordValidation: {
                required: this.$t("validation.password.require"),
                minLength: this.$t("validation.password.minLength"),
            },
            actions,
            returnUrl,
            recaptchaKey,
            recaptchaCode: null,
            hasReset: false,
            token: null,
        };
    },
    mounted() {
        window.captchaSumbit = this.register;
    },
    validations() {
        return {
            firstName: { required, alphaCyrillic },
            lastName: { required, alphaCyrillic },
            gender: { required },
            isAgree: { checked: (value) => value === true },
        };
    },
    methods: {
        termsHtml() {
            return this.$t("form.checkbox.terms").replace(
                "{0}",
                window.vm.userOfferUrl || "/Content/files/Д-оферта с Пользователем Платформы.pdf"
            );
        },
        agree(accepted) {
            this.canRegister = accepted;
        },
        scrollToError() {
            const errors = document.querySelectorAll(
                ".labeled-form-control__error, .base-select--incorrect, .input_state_incorrect"
            );

            if (errors.length > 0) {
                const firstError = errors[0];
                firstError.scrollIntoView({
                    block: "center",
                    behavior: "smooth",
                });
            }
        },
        async validate() {
            if (this.$refs.disabledCookieAlert.isActive) {
              this.showDisabledCookieBlock = true;
              return;
            }
            if (this.loading || !this.isAgree) {
                return;
            }

            this.canValidate = true;
            this.touch();

            if (!this.isValid) {
                this.shake();
                this.$nextTick(this.scrollToError);
                return;
            }

            if (!this.recaptchaKey) {
                return await this.register();
            }

            if (await this.checkEmail()) {
                window.grecaptcha.execute();
            } else {
                this.$nextTick(() => {
                    this.shake();
                    this.scrollToError();
                });
            }
        },

        async checkEmail() {
            this.isLoading = true;
            let result = false;
            try {
                const checkEmailData = new FormData();
                checkEmailData.set("email", this.$refs.emailControl.emailValue);

                const checkResult = axios
                    .post("/auth/checkEmail", checkEmailData)
                    .then((response) => {
                        const { data } = response;

                        this.isLoading = false;
                        result = !data.Exists;

                        return true;
                    });

                await Promise.all([checkResult]);

                return result;
            } catch (e) {
                console.log("error on checking email on server side");
            }
        },

        async register() {
            let token = null;
            if (this.recaptchaKey) {
                token = window.grecaptcha.getResponse();
                if (!token && !this.token) {
                    window.grecaptcha.reset();
                    return;
                }

                this.token = token;
            }

            this.loading = true;
            try {
                const birthDate = this.birthDate ? new Date(this.birthDate) : new Date();
                const authFormData = new FormData();
                authFormData.set("email", this.$refs.emailControl.emailValue);
                authFormData.set("password", this.password.value);
                authFormData.set("confirmPassword", this.password.value);
                authFormData.set("lastName", this.$v.lastName.$model);
                authFormData.set("firstName", this.$v.firstName.$model);
                authFormData.set("birthDay", birthDate.getDate());
                authFormData.set("birthMonth", birthDate.getMonth());
                authFormData.set("recaptchaCode", token);
                authFormData.set("birthYear", birthDate.getFullYear());
                authFormData.set("gender", this.gender === "male" ? "0" : "1");

                if (this.returnUrl) {
                    authFormData.set("returnUrl", this.returnUrl);
                }

                authFormData.set("sendPromoEmails", !!this.sendPromoEmails);

                const authResult = axios
                    .post(this.actions.registerUrl, authFormData)
                    .then((response) => {
                        const { data } = response;

                        if (data.Success) {
                            this.error = null;
                            this.completed = true;
                        } else {
                            if (!data.AlreadyExists) {
                                this.error = data.ErrorMessage;
                            } else {
                                this.shake();
                                this.scrollToError();
                            }

                            window.grecaptcha.reset();
                        }
                    });

                await Promise.all([authResult]);
            } catch (e) {
                console.log("error on register");
            }

            this.loading = false;
        },
        shake() {
            setTimeout(() => {
                this.shakeErrorInputs();
            }, 100);
        },
        getState(propName) {
            const validation = propName
                .split(".")
                .reduce(function (prev, curr) {
                    return prev ? prev[curr] : null;
                }, this.$v);

            return this.getStateByFlags(
                validation.$error || validation.$invalid,
                validation.$dirty
            );
        },
        getStateByFlags(hasError, isDirty) {
            if (!isDirty || !this.canValidate) {
                return "";
            }

            if (hasError) {
                return "incorrect";
            }

            return "correct";
        },
        touch() {
            this.$refs.firstNameControl.touch();
            this.$refs.lastNameControl.touch();
            this.$refs.emailControl.touch();
            this.$refs.passwordControl.touch();
        },
    },
    computed: {
        firstNameState() {
            return this.getState("firstName");
        },
        lastNameState() {
            return this.getState("lastName");
        },
        genderState() {
            return this.getStateByFlags(
                this.$v.gender.$error || this.$v.gender.$invalid,
                true
            );
        },
        isValid() {
            return (
                !this.$v.$anyError &&
                this.gender &&
                !this.dateValidation.hasErrors &&
                this.isAgree &&
                !this.$refs.emailControl.$v.$anyError &&
                !this.$refs.passwordControl.$v.$anyError
            );
        },
        visibleSendPromoEmails() {
            const disableTenants = ['Samarkand', 'BFLA', 'Legkovrace', 'topliga2'];
            return !disableTenants.includes(this.$tenant);
        },
    },
    watch: {
        visibleSendPromoEmails(val) {
            this.sendPromoEmails = val;
        }
    },
};
</script>

<style lang="scss">
@import "../../assets/tokens/tokens.scss";
@import "../../assets/normalize.min.css";
@import "createAccount.scss";
</style>
